import React from 'react'

// styles
import { avatarContainerVariants } from './styles'
import { ImageFit } from './ImageFit.component'
import { ImageProps } from 'next/image'
import { ImageResponsive } from './ImageResponsive.component'

export interface AvatarProps extends Partial<ImageProps> {
  image: string
  username?: string
  alt?: string
  size?: number | string
  variation?: keyof typeof avatarContainerVariants
  useLoaderBlur?: boolean
  className?: string
  responsive?: boolean
  customBorderRadius?: number
  imageFit?: 'cover' | 'contain'
}

export const Avatar: React.FC<AvatarProps> = ({
  image,
  username,
  variation = 'square',
  size = 144,
  useLoaderBlur = true,
  alt = 'avatar',
  className = '',
  responsive = false,
  onClick,
  customBorderRadius,
  imageFit = 'cover',
}) => {
  const defaultAvatar = '/assets/avatar.png'
  const srcImage = !!image ? image : defaultAvatar
  const alternativeText = username ? username : alt ? alt : ''

  const Component = responsive ? ImageResponsive : ImageFit
  const sizeResponsive = responsive ? '100%' : size
  const positionResponsive =
    responsive || size !== '100%' ? 'relative' : 'absolute'

  const borderRadius = React.useMemo(() => {
    if (customBorderRadius !== undefined) {
      return customBorderRadius
    }

    if (typeof size === 'string') {
      return 12
    }

    if (size < 40) {
      return 8
    } else if (size < 45) {
      return 10
    } else if (size < 144) {
      return 12
    } else {
      return 20
    }
  }, [size, customBorderRadius])

  return (
    <div
      style={{
        width: sizeResponsive,
        height: sizeResponsive,
        position: positionResponsive,
        borderRadius,
      }}
      className={`${avatarContainerVariants[variation]} ${className}`}
    >
      <Component
        key={`${srcImage}-${alternativeText}`}
        src={srcImage}
        alt={`${alternativeText} avatar`}
        blur={useLoaderBlur}
        blurSrc={srcImage}
        style={{
          objectFit: imageFit,
        }}
        errorSrc={defaultAvatar}
        onClick={onClick}
      />
    </div>
  )
}
